import React, { useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography
} from "@material-ui/core";
import { FileUpload } from "src/apps/commons/fileUploads";
import { yupTrimToNull } from "src/utils/stringUtils";
import { IRemyndVerifiedUserProductManualSaveReq } from "../types";

interface Props {
  label: string;
  open: boolean;
  defaultValue?: IRemyndVerifiedUserProductManualSaveReq;
  handleCloseModal: () => void;
  handleSubmitModal: (value: IRemyndVerifiedUserProductManualSaveReq) => void;
}

const schema = yup.object().shape({
  id: yup.string(),
  active: yup.boolean(),
  nameTh: yup.string().transform(yupTrimToNull).required("กรุณาระบุ ชื่อ(TH)"),
  nameEn: yup.string().transform(yupTrimToNull).required("กรุณาระบุ ชื่อ(EN)"),
  descTh: yup.string().transform(yupTrimToNull),
  descEn: yup.string().transform(yupTrimToNull),
  link: yup.string().transform(yupTrimToNull)
});

const UserProductManualModal: React.FC<Props> = props => {
  const { open, label, defaultValue, handleCloseModal, handleSubmitModal } =
    props;
  const defaultFormData = useMemo(
    () => ({
      id: defaultValue?.id ?? undefined,
      active: defaultValue?.active ?? true,
      nameTh: defaultValue?.nameTh ?? "",
      nameEn: defaultValue?.nameEn ?? "",
      descTh: defaultValue?.descTh ?? "",
      descEn: defaultValue?.descEn ?? "",
      link: defaultValue?.link ?? ""
    }),
    [defaultValue]
  );
  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<IRemyndVerifiedUserProductManualSaveReq>({
    defaultValues: defaultFormData,
    resolver: yupResolver(schema)
  });

  const [modalFileId, setFileId] = useState(defaultValue?.fileId);

  const handleTransformAndSubmit = (
    form: IRemyndVerifiedUserProductManualSaveReq
  ) => {
    handleSubmitModal({
      ...form,
      fileId: modalFileId
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="md"
      fullWidth
      scroll="body"
      aria-labelledby="form-dialog-title"
    >
      <form onSubmit={handleSubmit(handleTransformAndSubmit)}>
        <DialogTitle id="form-dialog-title" disableTypography>
          <Typography variant="h3">
            {!defaultValue ? "เพิ่ม " : "แก้ไข "}
            {label}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Controller
                name="nameTh"
                control={control}
                defaultValue={defaultValue?.nameTh ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="ชื่อ (TH) *"
                    error={Boolean(errors.nameTh)}
                    helperText={errors.nameTh?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="nameEn"
                control={control}
                defaultValue={defaultValue?.nameEn ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="ชื่อ (EN) *"
                    error={Boolean(errors.nameEn)}
                    helperText={errors.nameEn?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="descTh"
                control={control}
                defaultValue={defaultValue?.descTh ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="รายละเอียด (TH)"
                    error={Boolean(errors.descTh)}
                    helperText={errors.descTh?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Controller
                name="descEn"
                control={control}
                defaultValue={defaultValue?.descEn ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="รายละเอียด (EN)"
                    error={Boolean(errors.descEn)}
                    helperText={errors.descEn?.message}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider light />
            </Grid>

            <Grid item xs={12} md={6}>
              <Controller
                name="link"
                control={control}
                defaultValue={defaultValue?.link ?? ""}
                render={({ field }) => (
                  <TextField
                    variant="outlined"
                    fullWidth
                    {...field}
                    label="Link"
                    error={Boolean(errors.link)}
                    helperText={errors.link?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FileUpload
                label={"ไฟล์อัพโหลด"}
                fileId={modalFileId}
                onChange={setFileId}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Controller
                    name="active"
                    control={control}
                    defaultValue={defaultValue?.active}
                    render={({ field: { value, onChange } }) => (
                      <Checkbox
                        color="primary"
                        checked={value}
                        onChange={onChange}
                      />
                    )}
                  />
                }
                label="Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Cancel
          </Button>
          {!defaultValue ? (
            <Button
              key="addBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Add
            </Button>
          ) : (
            <Button
              key="updateBtn"
              variant="contained"
              color="primary"
              type="submit"
            >
              Update
            </Button>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default UserProductManualModal;
